import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthApiService } from '@pressteaze/auth';
import { MessagesService } from '@pressteaze/ui';
import { RecordAction } from './configuration.models';
import type {
	ConfigurationArea,
	ConfigurationTable,
	ConfigurationUser,
	OnlineBookingException,
	ScheduleException,
	StoreDomain,
} from './configuration.models';
import { ConfigurationStateService } from './configuration-state.service';

type Section = 'store' | 'spaces' | 'hours' | 'users' | 'domains';
type Editor =
	| 'store'
	| 'area'
	| 'table'
	| 'hour'
	| 'online-hour'
	| 'exception'
	| 'online-exception'
	| 'user'
	| 'domain';
type WindowDraft = {
	weekday?: number;
	open_time: string;
	close_time: string;
	sort_order: number;
};
type OnlineWindowDraft = {
	weekday?: number;
	first_start_time: string;
	last_start_time: string;
	sort_order: number;
};

@Component({
	selector: 'app-configuration-home',
	imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule],
	templateUrl: './configuration-home.component.html',
	styleUrl: './configuration-home.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})

export class ConfigurationHomeComponent implements OnInit {
	readonly RecordAction = RecordAction;
	readonly state = inject(ConfigurationStateService);
	private readonly auth = inject(AuthApiService);
	private readonly router = inject(Router);
	private readonly messagesSrv = inject(MessagesService);
	readonly section = signal<Section>('store');
	readonly editor = signal<Editor | null>(null);
	readonly weekdays = [
		'Κυριακή',
		'Δευτέρα',
		'Τρίτη',
		'Τετάρτη',
		'Πέμπτη',
		'Παρασκευή',
		'Σάββατο',
	];
	readonly roles = ['admin', 'manager', 'operator'] as const;

	storeDraft = this.emptyStore();
	areaDraft = this.emptyArea();
	tableDraft = this.emptyTable();
	hourDrafts: WindowDraft[] = [];
	onlineHourDrafts: OnlineWindowDraft[] = [];
	hourDraft: WindowDraft = this.emptyHour();
	onlineHourDraft: OnlineWindowDraft = this.emptyOnlineHour();
	editingHourIndex: number | null = null;
	editingOnlineHourIndex: number | null = null;
	exceptionDraft = this.emptyException();
	onlineExceptionDraft = this.emptyOnlineException();
	userDraft = this.emptyUser();
	domainDraft = { id: null as string | null, domain: '', is_primary: false };
	ownershipTarget = '';

	async ngOnInit(): Promise<void> {
		await this.state.load();
		this.syncFromContext();
	}
	setSection(section: Section): void {
		this.closeEditor();
		this.section.set(section);
	}
	async selectStore(storeId: string): Promise<void> {
		await this.state.load(storeId);
		this.syncFromContext();
	}

	newStore(): void {
		this.storeDraft = this.emptyStore();
		this.editor.set('store');
	}
	editStore(store = this.state.selectedStore()): void {
		if (store) this.storeDraft = { ...store };
		if (store) this.editor.set('store');
	}
	async saveStore(): Promise<void> {
		if (
			await this.state.run(
				{ action: 'save-store', ...this.storeDraft },
				'Οι ρυθμίσεις καταστήματος αποθηκεύτηκαν.',
			)
		) {
			this.closeEditor();
			this.syncFromContext();
		}
	}

	newArea(): void {
		this.areaDraft = this.emptyArea();
		this.editor.set('area');
	}
	editArea(area: ConfigurationArea): void {
		this.areaDraft = { ...area };
		this.editor.set('area');
	}
	resetArea(): void {
		this.areaDraft = this.emptyArea();
	}
	async saveArea(): Promise<void> {
		const storeId = this.state.context()?.selected_store_id;
		if (
			storeId &&
			(await this.state.run(
				{ action: 'save-area', store_id: storeId, ...this.areaDraft },
				'Ο χώρος αποθηκεύτηκε.',
			))
		) {
			this.areaDraft = this.emptyArea();
			this.closeEditor();
		}
	}
	newTable(): void {
		this.tableDraft = this.emptyTable();
		this.editor.set('table');
	}
	editTable(table: ConfigurationTable): void {
		this.tableDraft = { ...table };
		this.editor.set('table');
	}
	resetTable(): void {
		this.tableDraft = this.emptyTable();
	}
	areaName(areaId: string): string {
		return (
			this.state.context()?.areas.find((area) => area.id === areaId)?.name ??
			'Άγνωστος χώρος'
		);
	}
	async saveTable(): Promise<void> {
		const storeId = this.state.context()?.selected_store_id;
		if (
			storeId &&
			(await this.state.run(
				{ action: 'save-table', store_id: storeId, ...this.tableDraft },
				'Το τραπέζι αποθηκεύτηκε.',
			))
		) {
			this.tableDraft = this.emptyTable();
			this.closeEditor();
		}
	}

	addHour(weekday = 1): void {
		this.editingHourIndex = null;
		this.hourDraft = {
			...this.emptyHour(),
			weekday,
			sort_order: this.nextSort(this.hourDrafts, weekday),
		};
		this.editor.set('hour');
	}
	editHour(index: number): void {
		this.editingHourIndex = index;
		this.hourDraft = { ...this.hourDrafts[index] };
		this.editor.set('hour');
	}
	async saveHour(): Promise<void> {
		const next = [...this.hourDrafts];
		if (this.editingHourIndex === null) next.push({ ...this.hourDraft });
		else next[this.editingHourIndex] = { ...this.hourDraft };
		if (await this.saveHours(next)) {
			this.hourDrafts = next;
			this.closeEditor();
		}
	}
	removeHour(index: number): void {
		this.hourDrafts = this.hourDrafts.filter(
			(_, itemIndex) => itemIndex !== index,
		);
	}
	async deleteHour(index: number): Promise<void> {
		const next = this.hourDrafts.filter((_, itemIndex) => itemIndex !== index);
		if (await this.saveHours(next)) this.hourDrafts = next;
	}
	async saveHours(windows = this.hourDrafts): Promise<boolean> {
		const storeId = this.state.context()?.selected_store_id;
		return !!storeId &&
			(await this.state.run(
				{ action: 'save-hours', store_id: storeId, windows },
				'Το εβδομαδιαίο ωράριο αποθηκεύτηκε.',
			));
	}
	isOvernight(window: WindowDraft): boolean {
		return window.close_time < window.open_time;
	}
	addOnlineHour(weekday = 2): void {
		this.editingOnlineHourIndex = null;
		this.onlineHourDraft = {
			...this.emptyOnlineHour(),
			weekday,
			sort_order: this.nextOnlineSort(this.onlineHourDrafts, weekday),
		};
		this.editor.set('online-hour');
	}
	editOnlineHour(index: number): void {
		this.editingOnlineHourIndex = index;
		this.onlineHourDraft = { ...this.onlineHourDrafts[index] };
		this.editor.set('online-hour');
	}
	async saveOnlineHour(): Promise<void> {
		const next = [...this.onlineHourDrafts];
		if (this.editingOnlineHourIndex === null)
			next.push({ ...this.onlineHourDraft });
		else next[this.editingOnlineHourIndex] = { ...this.onlineHourDraft };
		if (await this.saveOnlineHours(next)) {
			this.onlineHourDrafts = next;
			this.closeEditor();
		}
	}
	removeOnlineHour(index: number): void {
		this.onlineHourDrafts = this.onlineHourDrafts.filter(
			(_, itemIndex) => itemIndex !== index,
		);
	}
	async deleteOnlineHour(index: number): Promise<void> {
		const next = this.onlineHourDrafts.filter(
			(_, itemIndex) => itemIndex !== index,
		);
		if (await this.saveOnlineHours(next)) this.onlineHourDrafts = next;
	}
	async saveOnlineHours(windows = this.onlineHourDrafts): Promise<boolean> {
		const storeId = this.state.context()?.selected_store_id;
		return !!storeId &&
			(await this.state.run(
				{
					action: 'save-online-booking-hours',
					store_id: storeId,
					windows,
				},
				'Το εβδομαδιαίο ωράριο online κρατήσεων αποθηκεύτηκε.',
			));
	}
	isOnlineOvernight(window: OnlineWindowDraft): boolean {
		return window.last_start_time < window.first_start_time;
	}

	addExceptionWindow(): void {
		this.exceptionDraft.windows.push({
			open_time: '18:00',
			close_time: '23:00',
			sort_order: (this.exceptionDraft.windows.length + 1) * 100,
		});
	}
	removeExceptionWindow(index: number): void {
		this.exceptionDraft.windows.splice(index, 1);
	}
	editException(exception: ScheduleException): void {
		this.exceptionDraft = {
			id: exception.id,
			service_date: exception.service_date,
			is_closed: exception.is_closed,
			reason: exception.reason ?? '',
			windows: exception.store_schedule_exception_windows.map((window) => ({
				...window,
			})),
		};
		this.editor.set('exception');
	}
	async saveException(): Promise<void> {
		const storeId = this.state.context()?.selected_store_id;
		const windows = this.exceptionDraft.is_closed
			? []
			: this.exceptionDraft.windows;
		if (
			storeId &&
			(await this.state.run(
				{
					action: 'save-exception',
					store_id: storeId,
					service_date: this.exceptionDraft.service_date,
					is_closed: this.exceptionDraft.is_closed,
					reason: this.exceptionDraft.reason,
					windows,
				},
				'Η εξαίρεση ωραρίου αποθηκεύτηκε.',
			))
		) {
			this.exceptionDraft = this.emptyException();
			this.closeEditor();
		}
	}
	async deleteException(id: string): Promise<void> {
		const storeId = this.state.context()?.selected_store_id;
		if (storeId)
			await this.state.run(
				{ action: 'delete-exception', store_id: storeId, id },
				'Η εξαίρεση αφαιρέθηκε.',
			);
	}

	addOnlineExceptionWindow(): void {
		this.onlineExceptionDraft.windows.push({
			first_start_time: '18:00',
			last_start_time: '22:00',
			sort_order: (this.onlineExceptionDraft.windows.length + 1) * 100,
		});
	}
	removeOnlineExceptionWindow(index: number): void {
		this.onlineExceptionDraft.windows.splice(index, 1);
	}
	editOnlineException(exception: OnlineBookingException): void {
		this.onlineExceptionDraft = {
			id: exception.id,
			service_date: exception.service_date,
			is_disabled: exception.is_disabled,
			reason: exception.reason ?? '',
			windows: exception.online_booking_schedule_exception_windows.map(
				(window) => ({ ...window }),
			),
		};
		this.editor.set('online-exception');
	}
	async saveOnlineException(): Promise<void> {
		const storeId = this.state.context()?.selected_store_id;
		const windows = this.onlineExceptionDraft.is_disabled
			? []
			: this.onlineExceptionDraft.windows;
		if (
			storeId &&
			(await this.state.run(
				{
					action: 'save-online-booking-exception',
					store_id: storeId,
					service_date: this.onlineExceptionDraft.service_date,
					is_disabled: this.onlineExceptionDraft.is_disabled,
					reason: this.onlineExceptionDraft.reason,
					windows,
				},
				'Η εξαίρεση online κρατήσεων αποθηκεύτηκε.',
			))
		) {
			this.onlineExceptionDraft = this.emptyOnlineException();
			this.closeEditor();
		}
	}
	async deleteOnlineException(id: string): Promise<void> {
		const storeId = this.state.context()?.selected_store_id;
		if (storeId)
			await this.state.run(
				{
					action: 'delete-online-booking-exception',
					store_id: storeId,
					id,
				},
				'Η εξαίρεση online κρατήσεων αφαιρέθηκε.',
			);
	}

	newUser(): void {
		this.userDraft = this.emptyUser();
		this.editor.set('user');
	}
	editUser(user: ConfigurationUser): void {
		if (!this.canEditUser(user)) return;
		this.userDraft = {
			id: user.id,
			full_name: user.full_name,
			username: user.username ?? '',
			contact_email: user.contact_email ?? '',
			email: '',
			password: '',
			role: user.role === 'owner' ? 'admin' : user.role,
			store_ids: user.user_stores.map((item) => item.store_id),
			default_store_id: user.default_store_id ?? '',
			pin: '',
			is_active: user.is_active,
		};
		this.editor.set('user');
	}
	canEditUser(user: ConfigurationUser): boolean {
		const currentUser = this.state.context()?.user;
		if (!currentUser || user.role === 'owner') return false;
		return (
			currentUser.role === 'owner' ||
			user.role !== 'admin' ||
			user.id === currentUser.id
		);
	}
	isEditingCurrentUser(): boolean {
		return this.userDraft.id === this.state.context()?.user.id;
	}
	resetUser(): void {
		this.userDraft = this.emptyUser();
	}
	toggleUserStore(storeId: string, checked: boolean): void {
		this.userDraft.store_ids = checked
			? [...new Set([...this.userDraft.store_ids, storeId])]
			: this.userDraft.store_ids.filter((id) => id !== storeId);
		if (!this.userDraft.store_ids.includes(this.userDraft.default_store_id))
			this.userDraft.default_store_id = '';
	}
	async saveUser(): Promise<void> {
		const action = this.userDraft.id ? 'update' : 'create';
		const body = {
			action,
			user_id: this.userDraft.id || undefined,
			...this.userDraft,
			default_store_id: this.userDraft.default_store_id || null,
			pin: this.userDraft.pin || undefined,
		};
		if (
			await this.state.run(
				body,
				this.userDraft.id
					? 'Ο χρήστης ενημερώθηκε.'
					: 'Ο χρήστης δημιουργήθηκε.',
				true,
			)
		) {
			this.userDraft = this.emptyUser();
			this.closeEditor();
		}
	}
	async deactivateUser(userId: string): Promise<void> {
		await this.state.run(
			{ action: 'deactivate', user_id: userId },
			'Ο χρήστης απενεργοποιήθηκε.',
			true,
		);
	}
	async setPin(userId: string): Promise<void> {
		const pin = window.prompt('Νέο PIN (4–8 ψηφία)');
		if (pin)
			await this.state.run(
				{ action: 'set-pin', user_id: userId, pin },
				'Το PIN ενημερώθηκε.',
				true,
			);
	}
	newException(): void {
		this.exceptionDraft = this.emptyException();
		this.editor.set('exception');
	}
	newOnlineException(): void {
		this.onlineExceptionDraft = this.emptyOnlineException();
		this.editor.set('online-exception');
	}

	newDomain(): void {
		this.domainDraft = { id: null, domain: '', is_primary: false };
		this.editor.set('domain');
	}
	editDomain(domain: StoreDomain): void {
		this.domainDraft = {
			id: domain.id,
			domain: domain.domain,
			is_primary: domain.is_primary,
		};
		this.editor.set('domain');
	}
	async saveDomain(): Promise<void> {
		const storeId = this.state.context()?.selected_store_id;
		if (
			storeId &&
			(await this.state.run(
				{
					action: this.domainDraft.id ? 'update-domain' : 'add-domain',
					store_id: storeId,
					...this.domainDraft,
				},
				this.domainDraft.id
					? 'Το domain ενημερώθηκε.'
					: 'Το DNS challenge δημιουργήθηκε.',
			))
		) {
			this.domainDraft = { id: null, domain: '', is_primary: false };
			this.closeEditor();
		}
	}
	async verifyDomain(domainId: string): Promise<void> {
		await this.state.run(
			{ action: 'verify-domain', domain_id: domainId },
			'Το domain επαληθεύτηκε. Η ενεργοποίηση SSL εκκρεμεί.',
		);
	}
	async refreshDomain(domainId: string): Promise<void> {
		await this.state.run(
			{ action: 'refresh-domain', domain_id: domainId },
			'Η κατάσταση του SSL ενημερώθηκε.',
		);
	}
	async copy(value: string): Promise<void> {
		await navigator.clipboard.writeText(value);
		this.messagesSrv.info('Αντιγράφηκε στο πρόχειρο.');
	}
	async transferOwnership(): Promise<void> {
		if (
			this.ownershipTarget &&
			(await this.state.run(
				{ action: 'transfer-ownership', target_user_id: this.ownershipTarget },
				'Η ιδιοκτησία μεταβιβάστηκε.',
			))
		)
			this.ownershipTarget = '';
	}
	async logout(): Promise<void> {
		await this.auth.logoutConfiguration();
		await this.router.navigate(['/login']);
	}
	closeEditor(): void {
		this.editor.set(null);
		this.editingHourIndex = null;
		this.editingOnlineHourIndex = null;
	}

	private syncFromContext(): void {
		const store = this.state.selectedStore();
		if (store) this.storeDraft = { ...store };
		this.hourDrafts =
			this.state.context()?.hours.map((window) => ({
				weekday: window.weekday,
				open_time: window.open_time.slice(0, 5),
				close_time: window.close_time.slice(0, 5),
				sort_order: window.sort_order,
			})) ?? [];
		this.onlineHourDrafts =
			this.state.context()?.online_booking_hours.map((window) => ({
				weekday: window.weekday,
				first_start_time: window.first_start_time.slice(0, 5),
				last_start_time: window.last_start_time.slice(0, 5),
				sort_order: window.sort_order,
			})) ?? [];
	}
	private nextSort(windows: WindowDraft[], weekday: number): number {
		return (
			(windows.filter((window) => window.weekday === weekday).length + 1) * 100
		);
	}
	private nextOnlineSort(
		windows: OnlineWindowDraft[],
		weekday: number,
	): number {
		return (
			(windows.filter((window) => window.weekday === weekday).length + 1) * 100
		);
	}
	private emptyStore() {
		return {
			id: null as string | null,
			name: '',
			slug: '',
			timezone: 'Europe/Athens',
			seating_duration_min: 90,
			booking_slot_interval_min: 30,
			min_booking_notice_min: 60,
			max_booking_days_ahead: 60,
			confirmation_required_above: null as number | null,
			is_active: true,
		};
	}
	private emptyArea() {
		return {
			id: null as string | null,
			name: '',
			is_active: true,
			is_customer_selectable: false,
			auto_assign_priority: 100,
			sort_order: 100,
		};
	}
	private emptyTable() {
		return {
			id: null as string | null,
			name: '',
			area_id: '',
			seats: 2,
			is_active: true,
		};
	}
	private emptyException() {
		return {
			id: null as string | null,
			service_date: '',
			is_closed: true,
			reason: '',
			windows: [] as WindowDraft[],
		};
	}
	private emptyOnlineException() {
		return {
			id: null as string | null,
			service_date: '',
			is_disabled: true,
			reason: '',
			windows: [] as OnlineWindowDraft[],
		};
	}
	private emptyHour(): WindowDraft {
		return { weekday: 1, open_time: '18:00', close_time: '23:00', sort_order: 100 };
	}
	private emptyOnlineHour(): OnlineWindowDraft {
		return {
			weekday: 2,
			first_start_time: '18:00',
			last_start_time: '22:00',
			sort_order: 100,
		};
	}
	private emptyUser() {
		return {
			id: null as string | null,
			full_name: '',
			username: '',
			contact_email: '',
			email: '',
			password: '',
			role: 'operator' as 'admin' | 'manager' | 'operator',
			store_ids: [] as string[],
			default_store_id: '',
			pin: '',
			is_active: true,
		};
	}
}
